import { createSlice } from "@reduxjs/toolkit";
import { User } from "../../types/auth.types";

export const ACCESS_TOKEN_KEY = "USER_ACCESS_TOKEN";

interface Role {
  id: number;
  name: string;
  code: string;
}

interface Organization {
  id: number;
  name: string;
}

interface Team {
  id: number;
  name: string;
}

interface Department {
  id: number;
  name: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  permissions: string[];
  roles: Role[];
  organizations: Organization[];
  teams: Team[];
  departmentsLeading: Department[];
  teamsLeading: Team[];
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
  isUserLogout: boolean;
}

const token = localStorage.getItem(ACCESS_TOKEN_KEY);

const initialState: AuthState = {
  user: null,
  token,
  permissions: [],
  roles: [],
  organizations: [],
  teams: [],
  departmentsLeading: [],
  teamsLeading: [],
  loading: false,
  profileLoading: !!token,
  error: null,
  isUserLogout: false,
};

const loginSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginPending(state) {
      state.loading = true;
      state.error = null;
    },
    loginSuccess(state, action) {
      state.loading = false;
      state.profileLoading = false;
      state.user = action.payload.user;
      state.token = action.payload.accessToken;
      state.permissions = action.payload.permissions ?? [];
      state.roles = action.payload.role ?? [];
      state.organizations = action.payload.organizations ?? [];
      state.teams = action.payload.teams ?? [];
      state.departmentsLeading = action.payload.departmentsLeading ?? [];
      state.teamsLeading = action.payload.teamsLeading ?? [];
      state.isUserLogout = false;
      state.error = null;
    },
    loginFailed(state, action) {
      state.loading = false;
      state.error = action.payload.message;
    },
    logoutUserSuccess(state) {
      localStorage.clear();
      state.user = null;
      state.token = null;
      state.permissions = [];
      state.roles = [];
      state.organizations = [];
      state.teams = [];
      state.departmentsLeading = [];
      state.teamsLeading = [];
      state.isUserLogout = true;
      state.loading = false;
      state.profileLoading = false;
    },
    setNewUserComplete(state) {
      if (state.user) {
        state.user = { ...state.user, new_user: 0 };
      }
    },
    /** Retake has begun — stop masking the survey state so the new run drives the UI. */
    setNewSurveyStarted(state) {
      if (state.user) {
        state.user = { ...state.user, new_survey: 0 };
      }
    },
  },
});

export const {
  loginPending,
  loginSuccess,
  loginFailed,
  logoutUserSuccess,
  setNewUserComplete,
  setNewSurveyStarted,
} = loginSlice.actions;

export default loginSlice.reducer;
